import { type PaletteKey, type PixelCanvas, sprite } from '../../lib';
import { outline2 } from './minion.sprite';

/** Paints the ellipse at (cx, cy) with `pick`'s colour for each pixel (normalised position; `rim` on its lit edge). */
function shape(
  c: PixelCanvas,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  pick: (nx: number, ny: number, rim: boolean) => PaletteKey | null,
): void {
  const out = (x: number, y: number) => ((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2 > 1;
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++)
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      if (out(x, y)) continue;
      const p = pick((x + 0.5 - cx) / rx, (y + 0.5 - cy) / ry, out(x - 1, y) || out(x, y - 1));
      if (p) c.set(x, y, p);
    }
}

/**
 * Straw at (along, across): strands run along `along`, each strand broken into lengths of 5-9 px that take a tone of
 * their own (mostly gold, some lit, some in shade), so it reads as combed straw, not stripes or speckle.
 */
const straw = (along: number, across: number, shade: boolean): PaletteKey => {
  const seg = Math.floor((along + rnd(across, 3) * 9) / (4 + Math.floor(rnd(across, 5) * 5)));
  const h = rnd(across, seg, 11);
  if (shade) return h < 0.35 ? 'u' : 'U';
  return h < 0.22 ? 'y' : h < 0.45 ? 'U' : 'Y';
};

/** A well-mixed hash in [0, 1) of two integers and a salt. */
function rnd(a: number, b: number, s = 0): number {
  let n = (Math.imul(a, 374761393) + Math.imul(b, 668265263) + Math.imul(s, 1274126177)) >>> 0;
  n = Math.imul(n ^ (n >>> 13), 1274126177) >>> 0;
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

/** The pole from row y0 down to the ground (row 91), lit on its left. */
function pole(c: PixelCanvas, y0: number): void {
  for (let y = y0; y <= 91; y++)
    c.set(29, y, 't').set(30, y, 'm').set(31, y, 'm').set(32, y, 'm').set(33, y, 'n').set(34, y, 'n');
}

/**
 * Straw spilling out of a bundle's end at column x, rows y0 … y1, towards `dir` (-1 left, 1 right): a fan that opens up
 * and down by `open` over `reach` px, its strands ending in a ragged edge with lit tips.
 */
function tuft(c: PixelCanvas, x: number, y0: number, y1: number, dir: number, reach: number, open = 0.3): void {
  for (let k = 0; k <= reach; k++) {
    const top = Math.round(y0 - k * open);
    const bottom = Math.round(y1 + k * open);
    for (let y = top; y <= bottom; y++) {
      const len = reach - Math.floor(rnd(y, dir, 7) * 4) - (y === top || y === bottom ? 1 : 0);
      if (k > len) continue;
      c.set(x + dir * k, y, k === len ? 'y' : straw(k, y, y > (y0 + y1) / 2 + 2 + k * open * 0.5));
    }
  }
}

function scarecrow(c: PixelCanvas): void {
  pole(c, 66);
  // The beam's arms: straw stuffed along it (strands running across), tied at the wrists, tufts at the ends.
  for (let x = 10; x <= 53; x++) for (let y = 50; y <= 61; y++) c.set(x, y, y === 50 ? 'y' : straw(x, y, y >= 59));
  for (const x of [13, 14, 49, 50]) for (let y = 49; y <= 62; y++) c.set(x, y, y === 49 ? 't' : y >= 60 ? 'N' : 'n');
  tuft(c, 9, 50, 61, -1, 7);
  tuft(c, 54, 50, 61, 1, 7);
  // The body: a straw bundle (strands running down), tied at the waist, splaying out under the rope.
  for (let y = 48; y <= 66; y++) for (let x = 21; x <= 42; x++) c.set(x, y, x === 21 ? 'y' : straw(y, x, x > 38));
  for (let y = 67; y <= 76; y++) {
    const k = y - 67;
    for (let x = 21 - Math.round(k * 0.45); x <= 42 + Math.round(k * 0.45); x++) {
      const len = 6 + Math.floor(rnd(x, 21) * 4);
      if (k > len) continue;
      c.set(x, y, k === len ? 'y' : straw(k, x, x > 38 + k * 0.45 - 1));
    }
  }
  for (let x = 20; x <= 43; x++) c.set(x, 65, x < 26 ? 'm' : 'n').set(x, 66, 'N');
  c.set(31, 67, 'n').set(32, 68, 'n').set(30, 68, 'm');
  // The red scarf round the neck, its end hanging.
  for (let y = 43; y <= 49; y++)
    for (let x = 19; x <= 44; x++) c.set(x, y, y === 43 ? 'i' : x > 38 || y >= 48 ? 'R' : 'r');
  for (const x of [24, 30, 36]) c.vline(x, 44, 47, 'R');
  for (let y = 49; y <= 58; y++) {
    const x0 = 36 + Math.round((y - 49) * 0.3);
    for (let x = x0; x <= x0 + 4; x++) c.set(x, y, x === x0 ? 'r' : 'R');
  }
  c.hline(37, 41, 59, 'N');
  // The sack head: burlap, a woven texture of 1-px lines, gathered at the neck.
  shape(c, 32, 31, 14, 12.5, (nx, ny, rim) => {
    if (rim && nx + ny < 0.3) return 'e';
    if (nx * 0.55 + ny * 0.85 > 0.62) return 't';
    return 'e';
  });
  for (let y = 23; y <= 41; y += 5)
    for (let x = 20; x <= 44; x++) if ((x + y) % 5 < 2) c.tint(x, y, c.get(x, y) === 't' ? 'm' : 't');
  // Face: button eyes (a glint each), a stitched smile.
  c.stamp(FACE, 22, 27);
  // The hat: a slate cone, its tip flopped over, a wide brim, a patch sewn on.
  for (let y = 4; y <= 20; y++) {
    const half = 2 + Math.round((y - 4) * 0.62);
    const lean = Math.round((20 - y) * 0.25);
    for (let x = 32 - half + lean; x <= 31 + half + lean; x++) {
      const u = (x - (32 + lean)) / half;
      c.set(x, y, u < -0.45 ? '3' : u < 0.45 ? '2' : '1');
    }
  }
  for (const [x, y] of [
    [36, 2],
    [37, 2],
    [38, 3],
    [39, 3],
    [40, 4],
    [40, 5],
    [36, 3],
    [37, 3],
    [38, 4],
    [39, 4],
  ] as const)
    c.set(x, y, y < 4 ? '3' : '2');
  shape(c, 32, 21, 20, 4.2, (nx, ny, rim) => (rim && ny < 0 ? '4' : ny > 0.45 ? '1' : nx < -0.2 ? '3' : '2'));
  // The band and the patch.
  for (let x = 23; x <= 41; x++) c.set(x, 18, x < 28 ? 'U' : 'u');
  c.rect(26, 9, 6, 5, '4').hline(26, 31, 13, '3');
  for (const [x, y] of [
    [25, 9],
    [25, 11],
    [25, 13],
    [32, 10],
    [32, 12],
    [27, 8],
    [30, 8],
  ] as const)
    c.set(x, y, 'k');
}

/** Button eyes and a stitched smile on the sack (20×10 at (22, 27)). */
const FACE = `
  ..kkk..........kkk..
  .kkkkk........kkkkk.
  .k3kkk........k3kkk.
  .kkkkk........kkkkk.
  ..kkk..........kkk..
  ....................
  ..k..k..k..k..k..k..
  ...kkkkkkkkkkkkkk...
  ..k..k..k..k..k..k..
  ....................
`;

function broken(c: PixelCanvas): void {
  // The snapped pole and the straw spilled either side of it.
  pole(c, 72);
  c.set(29, 71, 't').set(30, 70, 'e').set(31, 71, 'm').set(32, 69, 't').set(33, 70, 'm').set(34, 71, 'n');
  for (const [cx, dir] of [
    [15, -1],
    [48, 1],
  ] as const) {
    // A low heap of straw lying along the ground, loose straws thrown across it.
    shape(c, cx, 92, 11, 5.5, (nx, ny) =>
      ny < 0 ? straw(Math.round(nx * 11) + 20, Math.round(ny * 5.5) + 92, nx * dir > 0.45 || ny > -0.25) : null,
    );
    for (let k = 0; k < 5; k++) {
      const x0 = cx - 8 + k * 4;
      const y0 = 88 + Math.round(rnd(cx, k) * 2);
      const dx = (k % 2 ? 1 : -1) * (4 + Math.round(rnd(k, cx, 3) * 4));
      c.line(x0, y0, x0 + dx, y0 - 3, 'y');
    }
  }
}

export default sprite({
  name: 'scarecrow',
  group: 'actor',
  size: [64, 96],
  origin: [32, 92],
  density: 4,
  flash: true,
  notes: `Training dummy. 4× density (ADR-0111 follow-up): 64×96 art px = the old 16×24 world px, taller than a player so
it reads as a target. Classic 2-px k outline. Cross silhouette: straw stuffed along the beam (strands running across,
tied at the wrists, tufts fanning out at both ends), a straw body (strands running down) tied at the waist and splaying
into a ragged fringe, a lit pole; a burlap sack head (e, t shade, a sparse woven line pattern) with button eyes (a 3
glint) and a stitched smile; a red scarf with a hanging end; a slate pointed hat with a flopped tip, a wide brim, a rust
band and a patch sewn on. Straw is gold — yellow keeps targets distinct from blue-denim players — combed from strands
broken into 4-8 px lengths of their own tone (Y, some y lit, some U, u in shade), not stripes or speckle. Wobble and
squash come from the renderer's spring, not frames. broken: the pole snapped to a splintered stump, the straw spilled
in two low heaps either side.
Rubric: S5 R5 P4 L4 C4 F4. Weakest: the straw is busy at 8× (calm at 1:1); the hat's flopped tip is a stub; the broken
heaps are flat strips.`,
  anims: {
    idle: {
      frames: [
        (c: PixelCanvas) => {
          scarecrow(c);
          outline2(c);
        },
      ],
    },
    broken: {
      frames: [
        (c: PixelCanvas) => {
          broken(c);
          outline2(c);
        },
      ],
    },
  },
});
